import React, { useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { FileDownloadOutlined, TextSnippetOutlined } from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { detailCardSx } from "components/DesignSystem/RequestDetailField";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { SimpleTable, monoSx, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { pfSettings, sampleMembers, codeForLocation } from "./pfReportsSampleMembers";
import { CODES, ecrFileName, ecrLine, formatDay, money, num } from "./pfReportsData";

// ECR File (P9): one ECR 2.0 text file per PF code per month, with the
// values to type on the EPFO upload screen. Generate stays off while the
// code has a Blocking validation issue; regenerating makes a new version
// and the old one Superseded.

const FILE_COLORS = { Current: "#12B76A", Superseded: "#6B7280" };
const PREVIEW_LINES = 4;
const linkSx = { color: designColors.tealDark, fontWeight: designWeights.semibold, cursor: "pointer", border: 0, p: 0, background: "none", font: "inherit" };

const download = (name, text) => {
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
};

const Line = ({ label, value }) => (
  <Mui.Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.75, borderBottom: `1px solid ${designColors.slate100}`, "&:last-child": { borderBottom: "none" } }}>
    <Mui.Typography sx={{ ...designType.tableBodyCell, color: designColors.slate600 }}>{label}</Mui.Typography>
    <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold, textAlign: "right" }}>{value}</Mui.Typography>
  </Mui.Stack>
);

export const PfEcrTab = ({ month, monthInfo, codeFilter, register, blockingFor, ecrFiles, onGenerate, goTo }) => {
  const [codeId, setCodeId] = useState(() => (codeFilter !== "all" ? Number(codeFilter) : CODES[0].id));
  const [textView, setTextView] = useState(null);
  const code = CODES.find((item) => item.id === codeId);
  const members = register.filter((row) => row.code_id === codeId);
  const ready = members.filter((row) => row.uan);
  const blockers = blockingFor(codeId);
  const fileName = ecrFileName(code, month);
  const text = ready.map(ecrLine).join("\n");
  const settings = pfSettings();
  const notInPf = sampleMembers().filter((member) => member.status !== "Member" && codeForLocation(member.location)?.id === codeId);
  const files = ecrFiles.filter((file) => file.code_id === codeId || codeFilter === "all").sort((a, b) => moment(b.generated_at).diff(moment(a.generated_at)));
  const current = ecrFiles.find((file) => file.code_id === codeId && file.month === month && file.status === "Current");

  const generate = () => {
    onGenerate(codeId, ready.length);
    showToast("success", `${fileName} generated${current ? ` as version ${current.version + 1}; version ${current.version} is superseded` : ""}.`);
  };
  const fileText = (file) => register.filter((row) => row.code_id === file.code_id && row.uan).map(ecrLine).join("\n");
  const fileLabel = (file) => `${CODES.find((item) => item.id === file.code_id)?.type === "MAIN" ? "Main" : "Extension"} · ${moment(`${file.month}-01`).format("MMM YYYY")} · v${file.version}`;

  return (
    <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 3fr) minmax(0, 2fr)" }, gap: 2, alignItems: "start" }}>
      <Mui.Stack spacing={1.5}>
        <Mui.Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {CODES.map((item) => (
            <Mui.Chip
              key={item.id}
              label={`${item.type === "MAIN" ? "Main" : "Extension"} · ${item.code}`}
              onClick={() => setCodeId(item.id)}
              variant={item.id === codeId ? "filled" : "outlined"}
              sx={{
                fontWeight: designWeights.semibold,
                ...(item.id === codeId ? { backgroundColor: designColors.tealDark, color: designColors.white, "&:hover": { backgroundColor: designColors.tealDark } } : {}),
              }}
            />
          ))}
        </Mui.Stack>
        {blockers.length ? (
          <RequestInfoBanner
            tone="danger"
            title="Blocked"
            body={`${blockers.map((issue) => `${issue.name} (${issue.emp_id}) ${issue.rule === "UAN_MISSING" ? "has no UAN" : "has no date of birth"}`).join(" and ")}. Fix them in Statutory › PF › Members, then generate. The preview shows the lines ready so far.`}
            action={<SecondaryButton onClick={() => goTo("validation")} sx={{ whiteSpace: "nowrap" }}>See validation</SecondaryButton>}
          />
        ) : null}

        <Mui.Box sx={detailCardSx}>
          <Mui.Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
            <Mui.Typography sx={designType.sectionHeading}>Preview — {fileName}</Mui.Typography>
            <Mui.Typography sx={designType.captionText}>ECR 2.0 · text file · fields separated by #~#</Mui.Typography>
          </Mui.Stack>
          <SimpleTable
            fit
            heads={[
              { label: "UAN", width: 120 },
              { label: "Name", width: 140 },
              { label: "Gross Wages", align: "right", width: 90 },
              { label: "EPF Wages", align: "right", width: 90 },
              { label: "EPS Wages", align: "right", width: 90 },
              { label: "EDLI Wages", align: "right", width: 90 },
              { label: "EE Share", align: "right", width: 80 },
              { label: "EPS", align: "right", width: 70 },
              { label: "ER Share", align: "right", width: 80 },
              { label: "NCP Days", align: "right", width: 70 },
              { label: "Refund", align: "right", width: 60 },
            ]}
          >
            {ready.slice(0, PREVIEW_LINES).map((row) => (
              <Mui.TableRow key={row.id}>
                <Mui.TableCell sx={tableCellSx}>{row.uan}</Mui.TableCell>
                <Mui.TableCell sx={tableCellSx}>{row.name.toUpperCase()}</Mui.TableCell>
                {[row.gross, row.epf_wage, row.eps_wage, row.edli_wage, row.ee_pf + row.vpf, row.er_eps, row.er_epf, row.unpaid_days, 0].map((value, index) => (
                  <Mui.TableCell key={index} align="right" sx={tableCellSx}>{value}</Mui.TableCell>
                ))}
              </Mui.TableRow>
            ))}
            {ready.length > PREVIEW_LINES ? (
              <Mui.TableRow>
                <Mui.TableCell sx={tableCellSx}>…</Mui.TableCell>
                <Mui.TableCell colSpan={10} sx={{ ...tableCellSx, color: designColors.slate600 }}>{num(ready.length - PREVIEW_LINES)} more lines</Mui.TableCell>
              </Mui.TableRow>
            ) : null}
          </SimpleTable>
          <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: "center" }} sx={{ mt: 1.5 }}>
            <Mui.Typography sx={designType.helperText}>
              {num(ready.length)} lines · EPF wages {money(ready.reduce((total, row) => total + row.epf_wage, 0))}
            </Mui.Typography>
            <Mui.Stack direction="row" spacing={1.5} alignItems="center">
              <SecondaryButton startIcon={<TextSnippetOutlined />} onClick={() => setTextView({ name: fileName, text })}>View as text</SecondaryButton>
              {blockers.length ? (
                <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626" }}>Fix {blockers.length} blocking issue{blockers.length === 1 ? "" : "s"} to enable</Mui.Typography>
              ) : null}
              <PrimaryButton disabled={Boolean(blockers.length) || !ready.length} onClick={generate}>
                {current ? "Regenerate File" : "Generate File"}
              </PrimaryButton>
            </Mui.Stack>
          </Mui.Stack>
        </Mui.Box>
      </Mui.Stack>

      <Mui.Stack spacing={1.5}>
        <Mui.Box sx={detailCardSx}>
          <Mui.Typography sx={designType.sectionHeading}>For the EPFO upload screen</Mui.Typography>
          <Mui.Typography sx={{ ...designType.helperText, mt: 0.25, mb: 1 }}>Values to type on the portal</Mui.Typography>
          <Line label="Wage month" value={moment(`${month}-01`).format("MMMM YYYY")} />
          <Line label="Salary paid on" value={formatDay(monthInfo.paidOn)} />
          <Line label="Contribution rate" value={`${Math.round(settings.eeRate * 100)}%`} />
          <Line label="Members in file" value={num(members.length)} />
          <Line label="Not in PF (count · gross)" value={`${notInPf.length} · ${money(notInPf.reduce((total, member) => total + Math.round(member.pf_wage * 1.6), 0))}`} />
        </Mui.Box>

        <Mui.Box sx={detailCardSx}>
          <Mui.Typography sx={{ ...designType.sectionHeading, mb: 1.5 }}>Generated Files</Mui.Typography>
          <SimpleTable
            fit
            heads={[
              { label: "File", width: 170 },
              { label: "Lines", align: "right", width: 60 },
              { label: "Generated", width: 100 },
              { label: "Status", width: 100 },
              { label: "", width: 110 },
            ]}
          >
            {files.length ? (
              files.map((file) => {
                const fileCode = CODES.find((item) => item.id === file.code_id);
                const name = ecrFileName(fileCode, file.month);
                return (
                  <Mui.TableRow key={file.id}>
                    <Mui.TableCell sx={tableCellSx}>{fileLabel(file)}</Mui.TableCell>
                    <Mui.TableCell align="right" sx={tableCellSx}>{file.lines}</Mui.TableCell>
                    <Mui.TableCell sx={tableCellSx}>{formatDay(file.generated_at)}</Mui.TableCell>
                    <Mui.TableCell sx={tableCellSx}>
                      <RequestStatusChip status={file.status} colorCode={FILE_COLORS[file.status]} />
                    </Mui.TableCell>
                    <Mui.TableCell sx={tableCellSx}>
                      <Mui.Stack direction="row" spacing={1} alignItems="center">
                        <Mui.Box component="button" type="button" sx={linkSx} onClick={() => setTextView({ name, text: fileText(file) })}>View</Mui.Box>
                        <span>·</span>
                        <Mui.Tooltip title="Download" arrow>
                          <Mui.IconButton size="small" aria-label={`Download ${name}`} onClick={() => download(name, fileText(file))}>
                            <FileDownloadOutlined fontSize="small" />
                          </Mui.IconButton>
                        </Mui.Tooltip>
                      </Mui.Stack>
                    </Mui.TableCell>
                  </Mui.TableRow>
                );
              })
            ) : (
              <Mui.TableRow>
                <Mui.TableCell colSpan={5} sx={{ ...tableCellSx, textAlign: "center", color: designColors.slate600 }}>No files generated yet.</Mui.TableCell>
              </Mui.TableRow>
            )}
          </SimpleTable>
        </Mui.Box>

        <Mui.Box sx={detailCardSx}>
          <Mui.Typography sx={{ ...designType.sectionHeading, mb: 1 }}>Upload on the EPFO portal</Mui.Typography>
          {[
            "View and download the ECR file for the code",
            "EPFO portal → ECR return → enter the values above → upload (regular return first, every active member)",
            "Wait for the return to be approved → TRRN shown",
            "Generate the challan and pay",
            "Record the TRRN and challan in Reconciliation",
          ].map((step, index) => (
            <Mui.Typography key={step} sx={{ ...designType.helperText, py: 0.25 }}>
              {index + 1}. {step}
            </Mui.Typography>
          ))}
        </Mui.Box>
      </Mui.Stack>

      <Mui.Dialog open={Boolean(textView)} onClose={() => setTextView(null)} maxWidth="lg" fullWidth>
        <Mui.DialogTitle sx={{ ...designType.sectionHeading, fontSize: 16 }}>{textView?.name}</Mui.DialogTitle>
        <Mui.DialogContent dividers>
          <Mui.Box component="pre" sx={{ ...monoSx, fontSize: 12.5, m: 0, whiteSpace: "pre", overflowX: "auto", color: designColors.slate800 }}>
            {textView?.text || "No lines ready yet."}
          </Mui.Box>
        </Mui.DialogContent>
        <Mui.DialogActions sx={{ px: 3, py: 2 }}>
          <SecondaryButton onClick={() => setTextView(null)}>Close</SecondaryButton>
          <PrimaryButton startIcon={<FileDownloadOutlined />} disabled={!textView?.text} onClick={() => download(textView.name, textView.text)}>
            Download
          </PrimaryButton>
        </Mui.DialogActions>
      </Mui.Dialog>
    </Mui.Box>
  );
};
